import React, { useState, useEffect, useCallback } from 'react';
import { Users as UsersIcon, Search, UserPlus, UserMinus, Edit, RefreshCw, AlertTriangle, CheckCircle, Download, Gift, Calendar, Info, Globe, Eye } from 'lucide-react';
import api from '../../utils/api';
import Modal from '../../components/common/Modal';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/common/Toast';
import './Users.css';

const ROLE_OPTIONS = ['admin', 'manager', 'staff'];
const STATUS_LABELS = {
  active: 'Active',
  pending_setup: 'Pending Setup',
  deactivated: 'Deactivated',
};

function AdminUsers() {
  const { user: currentUser, startImpersonating } = useAuth();
  const toast = useToast();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [departments, setDepartments] = useState([]);
  const [countries, setCountries] = useState([]);

  // Edit modal
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [formErrorSummary, setFormErrorSummary] = useState('');

  // Department assignments (inside edit modal)
  const [editDeptAssignments, setEditDeptAssignments] = useState([]);
  const [deptSaving, setDeptSaving] = useState(false);

  // PTO section (inside edit modal)
  const [ptoBalance, setPtoBalance] = useState(null);
  const [ptoLoading, setPtoLoading] = useState(false);
  const [allocationAmount, setAllocationAmount] = useState('');
  const [allocationSaving, setAllocationSaving] = useState(false);
  const [bonusAmount, setBonusAmount] = useState('');
  const [bonusReason, setBonusReason] = useState('');
  const [bonusSaving, setBonusSaving] = useState(false);
  const [prorationHint, setProrationHint] = useState(null);

  // Holiday country assignments (inside edit modal, for managers/admins)
  const [holidayCountries, setHolidayCountries] = useState([]);
  const [holidayCountrySaving, setHolidayCountrySaving] = useState(false);

  // Deactivate modal
  const [deactivateModalOpen, setDeactivateModalOpen] = useState(false);
  const [deactivateUser, setDeactivateUser] = useState(null);
  const [deactivatePreview, setDeactivatePreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Import modal
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importConfigured, setImportConfigured] = useState(false);
  const [importConfigMessage, setImportConfigMessage] = useState('');
  const [importError, setImportError] = useState(null);
  const [importUsers, setImportUsers] = useState([]);
  const [selectedEmails, setSelectedEmails] = useState(new Set());
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importStats, setImportStats] = useState({ total: 0, existing: 0 });
  const [importSearchQuery, setImportSearchQuery] = useState('');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (roleFilter) params.set('role', roleFilter);
      if (searchQuery) params.set('search', searchQuery);

      const [usersRes, deptRes, countryRes] = await Promise.allSettled([
        api.get(`/users?${params.toString()}`),
        api.get('/departments'),
        api.get('/countries'),
      ]);

      setUsers(usersRes.status === 'fulfilled' ? (usersRes.value.users || []) : []);
      setDepartments(deptRes.status === 'fulfilled' ? (deptRes.value.departments || []) : []);
      setCountries(countryRes.status === 'fulfilled' ? (countryRes.value.countries || []) : []);
    } catch (err) {
      // Don't loop
    } finally {
      setLoading(false);
    }
  }, [statusFilter, roleFilter, searchQuery]);

  useEffect(() => {
    const timeout = setTimeout(loadData, 300);
    return () => clearTimeout(timeout);
  }, [loadData]);

  // Edit user -- loads full user detail, departments, and PTO balance
  const handleEditClick = async (user) => {
    setEditUser(user);
    setEditForm({
      role: user.role,
      country_id: user.country?.id || '',
      holiday_calendar_id: user.holiday_calendar?.id || '',
      birth_month: user.birth_month || '',
      birth_day: user.birth_day || '',
      start_date: user.start_date ? user.start_date.split('T')[0] : '',
      employment_type: user.employment_type || 'staff_remote',
      first_name: user.first_name || '',
      last_name: user.last_name || '',
    });
    setFieldErrors({});
    setFormErrorSummary('');
    setEditDeptAssignments(user.departments || []);
    setPtoBalance(null);
    setAllocationAmount('');
    setBonusAmount('');
    setBonusReason('');
    setProrationHint(null);
    setHolidayCountries([]);
    setEditModalOpen(true);

    // Load PTO balance and holiday country assignments in background
    loadUserPtoBalance(user.id, user.start_date);
    api.get(`/users/${user.id}/holiday-countries`)
      .then(res => setHolidayCountries((res.holiday_countries || []).map(hc => hc.country_id)))
      .catch(() => {});
  };

  const loadUserPtoBalance = async (userId, startDate) => {
    setPtoLoading(true);
    try {
      const balance = await api.get(`/users/${userId}/balance`);
      setPtoBalance(balance);

      // If allocation exists, pre-fill the input
      if (balance.allocation) {
        setAllocationAmount(String(balance.allocation));
      }

      // Check proration hint for mid-year hires
      if (startDate && balance.pto_year && balance.allocation === 0) {
        try {
          const prorationRes = await api.get(
            `/allocations/prorate?start_date=${startDate.split('T')[0]}&total_days=15&pto_year_id=${balance.pto_year.id}`
          );
          if (prorationRes.months_remaining < 12) {
            setProrationHint(prorationRes);
          }
        } catch {
          // Proration hint is optional
        }
      }
    } catch {
      // PTO balance loading is non-blocking
    } finally {
      setPtoLoading(false);
    }
  };

  const validateEditForm = () => {
    const errors = {};
    if (!editForm.first_name || !editForm.first_name.trim()) {
      errors.first_name = 'This field is required';
    }
    if (!editForm.last_name || !editForm.last_name.trim()) {
      errors.last_name = 'This field is required';
    }
    if (!editForm.role) {
      errors.role = 'This field is required';
    }
    if (!editForm.country_id) {
      errors.country_id = 'This field is required';
    }
    if (!editForm.birth_month) {
      errors.birth_month = 'Birth month is required';
    }
    if (!editForm.birth_day) {
      errors.birth_day = 'Birth day is required';
    }
    if (!editForm.employment_type) {
      errors.employment_type = 'This field is required';
    }
    return errors;
  };

  const handleEditFieldChange = (field, value) => {
    const updates = { [field]: value };

    // When country changes and holiday calendar matches the old country (or is empty),
    // auto-sync holiday calendar to the new country
    if (field === 'country_id') {
      const currentCalendar = editForm.holiday_calendar_id;
      const oldCountry = editForm.country_id;
      if (!currentCalendar || currentCalendar === oldCountry) {
        updates.holiday_calendar_id = value;
      }
    }

    setEditForm(prev => ({ ...prev, ...updates }));
    if (fieldErrors[field]) {
      setFieldErrors(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
      if (Object.keys(fieldErrors).length <= 1) {
        setFormErrorSummary('');
      }
    }
  };

  const handleEditSave = async () => {
    const errors = validateEditForm();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setFormErrorSummary('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      await api.put(`/users/${editUser.id}`, editForm);
      toast.success('User updated');
      setEditModalOpen(false);
      loadData();
    } catch (err) {
      // Handle server-side field-level validation errors
      if (err.fields) {
        setFieldErrors(err.fields);
        setFormErrorSummary(err.message || 'Validation failed');
      } else {
        toast.error(err.message || 'Failed to update user');
      }
    } finally {
      setSaving(false);
    }
  };

  // Activate
  const handleActivate = async (userId) => {
    try {
      await api.patch(`/users/${userId}/status`, { status: 'active' });
      toast.success('User activated');
      loadData();
    } catch (err) {
      toast.error(err.message || 'Failed to activate user');
    }
  };

  // Deactivate
  const handleDeactivateClick = async (user) => {
    setDeactivateUser(user);
    setDeactivatePreview(null);
    setLoadingPreview(true);
    setDeactivateModalOpen(true);
    try {
      const preview = await api.get(`/users/${user.id}/deactivation-preview`);
      setDeactivatePreview(preview);
    } catch (err) {
      // If preview fails, still allow deactivation but without details
      setDeactivatePreview(null);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleDeactivateConfirm = async () => {
    setSaving(true);
    try {
      const res = await api.patch(`/users/${deactivateUser.id}/status`, { status: 'deactivated' });
      const cleanup = res.cleanup || {};
      toast.success(
        `User deactivated. ${cleanup.pending_cancelled || 0} pending cancelled, ${cleanup.future_removed || 0} future removed.`
      );
      setDeactivateModalOpen(false);
      loadData();
    } catch (err) {
      toast.error(err.message || 'Failed to deactivate user');
    } finally {
      setSaving(false);
    }
  };

  // Department assignments (within edit modal)
  const handleAddDept = async (deptId) => {
    if (!editUser) return;
    setDeptSaving(true);
    try {
      const isPrimary = editDeptAssignments.length === 0; // First dept is primary
      await api.post(`/departments/${deptId}/members`, {
        user_id: editUser.id,
        is_manager: false,
        is_primary: isPrimary,
      });
      toast.success('Department assigned');
      // Refresh assignments
      const res = await api.get(`/users/${editUser.id}`);
      setEditDeptAssignments(res.user.departments || []);
      loadData();
    } catch (err) {
      toast.error(err.message || 'Failed to assign department');
    } finally {
      setDeptSaving(false);
    }
  };

  const handleToggleDeptManager = async (deptId, currentIsManager) => {
    if (!editUser) return;
    setDeptSaving(true);
    try {
      const assignment = editDeptAssignments.find(d => d.id === deptId);
      await api.post(`/departments/${deptId}/members`, {
        user_id: editUser.id,
        is_manager: !currentIsManager,
        is_primary: assignment?.is_primary || false,
      });
      // Refresh assignments
      const res = await api.get(`/users/${editUser.id}`);
      setEditDeptAssignments(res.user.departments || []);
      loadData();
    } catch (err) {
      toast.error(err.message || 'Failed to update department role');
    } finally {
      setDeptSaving(false);
    }
  };

  const handleSetPrimaryDept = async (deptId) => {
    if (!editUser) return;
    setDeptSaving(true);
    try {
      const assignment = editDeptAssignments.find(d => d.id === deptId);
      await api.post(`/departments/${deptId}/members`, {
        user_id: editUser.id,
        is_manager: assignment?.is_manager || false,
        is_primary: true,
      });
      // Refresh assignments
      const res = await api.get(`/users/${editUser.id}`);
      setEditDeptAssignments(res.user.departments || []);
      loadData();
    } catch (err) {
      toast.error(err.message || 'Failed to set primary department');
    } finally {
      setDeptSaving(false);
    }
  };

  // Import functions
  const loadImportUsers = async () => {
    setImportLoading(true);
    setImportError(null);
    setImportResult(null);
    try {
      const res = await api.get('/admin/google-directory');
      setImportConfigured(res.configured);
      setImportConfigMessage(res.message || '');
      setImportUsers(res.users || []);
      setImportStats({
        total: res.total_workspace || 0,
        existing: res.already_imported || 0,
      });
      if (res.error) {
        setImportError(res.error);
      }
    } catch (err) {
      setImportError('Failed to connect to the server');
    } finally {
      setImportLoading(false);
    }
  };

  const handleOpenImportModal = () => {
    setImportModalOpen(true);
    setSelectedEmails(new Set());
    setImportResult(null);
    setImportSearchQuery('');
    loadImportUsers();
  };

  const handleRefreshImport = () => {
    setImportSearchQuery('');
    setSelectedEmails(new Set());
    loadImportUsers();
  };

  // Filter import users by search query
  const filteredImportUsers = importUsers.filter(user => {
    if (!importSearchQuery.trim()) return true;
    const query = importSearchQuery.toLowerCase();
    return (
      (user.name || '').toLowerCase().includes(query) ||
      (user.email || '').toLowerCase().includes(query)
    );
  });

  const toggleSelect = (email) => {
    setSelectedEmails(prev => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const visibleEmails = filteredImportUsers.map(u => u.email);
    const allVisibleSelected = visibleEmails.length > 0 && visibleEmails.every(e => selectedEmails.has(e));
    if (allVisibleSelected) {
      // Deselect all visible users
      setSelectedEmails(prev => {
        const next = new Set(prev);
        visibleEmails.forEach(e => next.delete(e));
        return next;
      });
    } else {
      // Select all visible users
      setSelectedEmails(prev => {
        const next = new Set(prev);
        visibleEmails.forEach(e => next.add(e));
        return next;
      });
    }
  };

  const handleImport = async () => {
    if (selectedEmails.size === 0) return;

    const usersToImport = importUsers.filter(u => selectedEmails.has(u.email));

    setImporting(true);
    try {
      const res = await api.post('/admin/google-directory/import', {
        users: usersToImport,
      });

      setImportResult(res);

      if (res.imported?.length > 0) {
        toast.success(`${res.imported.length} user(s) imported successfully`);
      }
      if (res.skipped?.length > 0) {
        toast.warning(`${res.skipped.length} user(s) skipped (already exist)`);
      }

      // Refresh both the import list and the team list
      await loadImportUsers();
      setSelectedEmails(new Set());
      loadData();
    } catch (err) {
      toast.error(err.message || 'Failed to import users');
    } finally {
      setImporting(false);
    }
  };

  const handleRemoveDept = async (deptId) => {
    if (!editUser) return;
    setDeptSaving(true);
    try {
      await api.delete(`/departments/${deptId}/members/${editUser.id}`);
      toast.success('Department removed');
      setEditDeptAssignments(prev => prev.filter(d => d.id !== deptId));
      loadData();
    } catch (err) {
      toast.error(err.message || 'Failed to remove department');
    } finally {
      setDeptSaving(false);
    }
  };

  // PTO allocation
  const handleSetAllocation = async () => {
    if (!editUser || !ptoBalance?.pto_year) return;
    const amount = parseFloat(allocationAmount);
    if (isNaN(amount) || amount < 0) {
      toast.error('Please enter a valid number of days');
      return;
    }
    setAllocationSaving(true);
    try {
      await api.post('/allocations', {
        user_id: editUser.id,
        pto_year_id: ptoBalance.pto_year.id,
        total_days: amount,
      });
      toast.success('PTO allocation updated');
      loadUserPtoBalance(editUser.id, editUser.start_date);
    } catch (err) {
      toast.error(err.message || 'Failed to set allocation');
    } finally {
      setAllocationSaving(false);
    }
  };

  // Bonus award
  const handleAwardBonus = async () => {
    if (!editUser) return;
    const amount = parseFloat(bonusAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid number of bonus days');
      return;
    }
    if (!bonusReason.trim()) {
      toast.error('Please provide a reason for the bonus');
      return;
    }
    setBonusSaving(true);
    try {
      await api.post('/allocations/bonus', {
        user_id: editUser.id,
        days: amount,
        reason: bonusReason.trim(),
      });
      toast.success(`${amount} bonus day(s) awarded`);
      setBonusAmount('');
      setBonusReason('');
      loadUserPtoBalance(editUser.id, editUser.start_date);
    } catch (err) {
      toast.error(err.message || 'Failed to award bonus');
    } finally {
      setBonusSaving(false);
    }
  };

  return (
    <div className="admin-users">
      <div className="page-header">
        <UsersIcon size={24} strokeWidth={2} />
        <h1>Team Management</h1>
        <button className="btn-import-google" onClick={handleOpenImportModal}>
          <Download size={14} />
          Import from Google Workspace
        </button>
      </div>

      {/* Filters */}
      <div className="users-filters">
        <div className="search-input-wrap">
          <Search size={14} className="search-icon" />
          <input
            type="text"
            className="search-input"
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <select className="form-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All Statuses</option>
          {Object.entries(STATUS_LABELS).map(([val, label]) => (
            <option key={val} value={val}>{label}</option>
          ))}
        </select>

        <select className="form-select" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
          <option value="">All Roles</option>
          {ROLE_OPTIONS.map(r => (
            <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>
          ))}
        </select>
      </div>

      {/* Users list */}
      {loading ? (
        <div className="users-loading">Loading...</div>
      ) : (
        <div className="users-table">
          <div className="users-table-header">
            <span>Name</span>
            <span>Role</span>
            <span>Status</span>
            <span>Department(s)</span>
            <span>Country</span>
            <span>Actions</span>
          </div>

          {users.length === 0 ? (
            <div className="users-empty">No users found</div>
          ) : (
            users.map(u => (
              <div key={u.id} className="users-row">
                <div className="users-name-cell">
                  {u.avatar_url && <img src={u.avatar_url} alt="" className="users-avatar" />}
                  <div>
                    <span className="users-name">{u.name}</span>
                    <span className="users-email">{u.email}</span>
                  </div>
                </div>

                <span className="users-role-badge">{u.role}</span>

                <span className={`users-status users-status-${u.status}`}>
                  {STATUS_LABELS[u.status] || u.status}
                </span>

                <div className="users-depts">
                  {(u.departments || []).map(d => (
                    <span key={d.id} className={`dept-tag ${d.is_manager ? 'dept-tag-manager' : ''}`}>
                      {d.name}{d.is_manager ? ' (Mgr)' : ''}
                    </span>
                  ))}
                  {(!u.departments || u.departments.length === 0) && (
                    <span className="dept-none">None</span>
                  )}
                </div>

                <span className="users-country">{u.country?.code || '-'}</span>

                <div className="users-actions">
                  <button className="btn-icon" title="Edit" onClick={() => handleEditClick(u)}>
                    <Edit size={14} />
                  </button>
                  {u.status === 'active' && u.id !== currentUser?.id && (
                    <button
                      className="btn-icon"
                      title={`View as ${u.first_name || u.name}`}
                      onClick={async () => {
                        try {
                          await startImpersonating(u.id);
                          window.location.href = '/dashboard';
                        } catch (err) {
                          toast.error(err.message || 'Failed to start impersonation');
                        }
                      }}
                    >
                      <Eye size={14} />
                    </button>
                  )}
                  {u.status === 'pending_setup' && (
                    <button className="btn-icon btn-icon-success" title="Activate" onClick={() => handleActivate(u.id)}>
                      <UserPlus size={14} />
                    </button>
                  )}
                  {u.status === 'active' && (
                    <button className="btn-icon btn-icon-danger" title="Deactivate" onClick={() => handleDeactivateClick(u)}>
                      <UserMinus size={14} />
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Edit User Modal */}
      <Modal isOpen={editModalOpen} onClose={() => setEditModalOpen(false)} title="Edit User" size="large">
        {editUser && (
          <>
            <p className="modal-subtitle">{editUser.name} ({editUser.email})</p>

            {formErrorSummary && (
              <div className="form-error-summary">
                <AlertTriangle size={14} />
                {formErrorSummary}
              </div>
            )}

            {/* Section 1: Personal Info */}
            <div className="edit-section">
              <h3 className="edit-section-title">Personal Info</h3>
              <div className="edit-form-grid">
                <div className="form-group">
                  <label className="form-label">First Name <span className="required-indicator">*</span></label>
                  <input
                    type="text"
                    className={`form-input${fieldErrors.first_name ? ' field-error' : ''}`}
                    value={editForm.first_name}
                    onChange={(e) => handleEditFieldChange('first_name', e.target.value)}
                  />
                  {fieldErrors.first_name && <span className="field-error-message">{fieldErrors.first_name}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">Last Name <span className="required-indicator">*</span></label>
                  <input
                    type="text"
                    className={`form-input${fieldErrors.last_name ? ' field-error' : ''}`}
                    value={editForm.last_name}
                    onChange={(e) => handleEditFieldChange('last_name', e.target.value)}
                  />
                  {fieldErrors.last_name && <span className="field-error-message">{fieldErrors.last_name}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">Role <span className="required-indicator">*</span></label>
                  <select
                    className={`form-select${fieldErrors.role ? ' field-error' : ''}`}
                    value={editForm.role}
                    onChange={(e) => handleEditFieldChange('role', e.target.value)}
                    style={{ width: '100%' }}
                  >
                    {ROLE_OPTIONS.map(r => (
                      <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>
                    ))}
                  </select>
                  {fieldErrors.role && <span className="field-error-message">{fieldErrors.role}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">Country <span className="required-indicator">*</span></label>
                  <select
                    className={`form-select${fieldErrors.country_id ? ' field-error' : ''}`}
                    value={editForm.country_id}
                    onChange={(e) => handleEditFieldChange('country_id', e.target.value)}
                    style={{ width: '100%' }}
                  >
                    <option value="">Select country</option>
                    {countries.length > 0 ? (
                      countries.map(c => (
                        <option key={c.id} value={c.id}>{c.name} ({c.code})</option>
                      ))
                    ) : (
                      <option value="" disabled>No countries available</option>
                    )}
                  </select>
                  {fieldErrors.country_id && <span className="field-error-message">{fieldErrors.country_id}</span>}
                  {countries.length === 0 && (
                    <span className="field-hint-message">Countries have not been configured yet.</span>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    Holiday Calendar
                    <Globe size={12} className="field-label-icon" />
                  </label>
                  <select
                    className={`form-select${fieldErrors.holiday_calendar_id ? ' field-error' : ''}`}
                    value={editForm.holiday_calendar_id}
                    onChange={(e) => handleEditFieldChange('holiday_calendar_id', e.target.value)}
                    style={{ width: '100%' }}
                  >
                    <option value="">Same as country</option>
                    {countries.map(c => (
                      <option key={c.id} value={c.id}>{c.name} ({c.code})</option>
                    ))}
                  </select>
                  {fieldErrors.holiday_calendar_id && <span className="field-error-message">{fieldErrors.holiday_calendar_id}</span>}
                  {editForm.holiday_calendar_id && editForm.holiday_calendar_id !== editForm.country_id ? (
                    <span className="field-hint-message holiday-calendar-hint">
                      <Info size={10} />
                      This user follows a different holiday calendar than their assigned country.
                    </span>
                  ) : (
                    <span className="field-hint-message">
                      Override if this person follows a different country's holidays.
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">Birthday <span className="required-indicator">*</span></label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <select
                      className={`form-select${fieldErrors.birth_month ? ' field-error' : ''}`}
                      value={editForm.birth_month}
                      onChange={(e) => handleEditFieldChange('birth_month', e.target.value)}
                      style={{ flex: 1 }}
                    >
                      <option value="">Month</option>
                      {['January','February','March','April','May','June','July','August','September','October','November','December'].map((m, i) => (
                        <option key={i + 1} value={i + 1}>{m}</option>
                      ))}
                    </select>
                    <select
                      className={`form-select${fieldErrors.birth_day ? ' field-error' : ''}`}
                      value={editForm.birth_day}
                      onChange={(e) => handleEditFieldChange('birth_day', e.target.value)}
                      style={{ width: '80px' }}
                    >
                      <option value="">Day</option>
                      {Array.from({ length: 31 }, (_, i) => (
                        <option key={i + 1} value={i + 1}>{i + 1}</option>
                      ))}
                    </select>
                  </div>
                  {(fieldErrors.birth_month || fieldErrors.birth_day) && (
                    <span className="field-error-message">{fieldErrors.birth_month || fieldErrors.birth_day}</span>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">Start Date</label>
                  <input
                    type="date"
                    className="form-input"
                    value={editForm.start_date}
                    onChange={(e) => handleEditFieldChange('start_date', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Employment Type <span className="required-indicator">*</span></label>
                  <select
                    className={`form-select${fieldErrors.employment_type ? ' field-error' : ''}`}
                    value={editForm.employment_type}
                    onChange={(e) => handleEditFieldChange('employment_type', e.target.value)}
                    style={{ width: '100%' }}
                  >
                    <option value="staff_w2">Staff/W2</option>
                    <option value="staff_remote">Staff/Remote</option>
                  </select>
                  {fieldErrors.employment_type && <span className="field-error-message">{fieldErrors.employment_type}</span>}
                  {editUser
                    && editForm.employment_type !== (editUser.employment_type || 'staff_remote')
                    && ((ptoBalance?.used?.total > 0) || (ptoBalance?.days_taken_this_year > 0)) && (
                    <div style={{
                      marginTop: '8px',
                      padding: '8px 10px',
                      background: 'var(--amber-50, #fffbeb)',
                      border: '1px solid var(--amber-300, #fcd34d)',
                      borderRadius: '6px',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '8px',
                      fontSize: 'var(--font-size-sm)',
                      color: 'var(--amber-800, #92400e)',
                    }}>
                      <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: '2px' }} />
                      <span>
                        Switching employment type does not recalculate existing time off requests.
                        This user has prior requests, so their balance may be inaccurate.
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-actions">
                <button className="btn-secondary" onClick={() => setEditModalOpen(false)}>Cancel</button>
                <button className="btn-primary" onClick={handleEditSave} disabled={saving}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>

            {/* Section 2: Departments */}
            <div className="edit-section">
              <h3 className="edit-section-title">Departments</h3>

              {editDeptAssignments.length === 0 ? (
                <p className="edit-section-empty">No departments assigned</p>
              ) : (
                <div className="dept-assignment-list">
                  {editDeptAssignments.map(d => (
                    <div key={d.id} className="dept-assignment-row">
                      <span className="dept-assignment-name">{d.name}</span>
                      <div className="dept-assignment-controls">
                        <label className="dept-toggle-label">
                          <input
                            type="checkbox"
                            checked={d.is_manager}
                            onChange={() => handleToggleDeptManager(d.id, d.is_manager)}
                            disabled={deptSaving}
                          />
                          <span className="dept-toggle-text">Manager</span>
                        </label>
                        <label className="dept-toggle-label">
                          <input
                            type="radio"
                            name="primary_dept"
                            checked={d.is_primary}
                            onChange={() => handleSetPrimaryDept(d.id)}
                            disabled={deptSaving}
                          />
                          <span className="dept-toggle-text">Primary</span>
                        </label>
                        <button
                          className="dept-remove-btn"
                          onClick={() => handleRemoveDept(d.id)}
                          disabled={deptSaving}
                          title="Remove from department"
                        >
                          <UserMinus size={12} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Add to department */}
              {departments.filter(d => !editDeptAssignments.some(a => a.id === d.id)).length > 0 && (
                <div className="dept-add-section">
                  <label className="form-label">Add to department</label>
                  <div className="dept-add-options">
                    {departments
                      .filter(d => !editDeptAssignments.some(a => a.id === d.id))
                      .map(d => (
                        <button
                          key={d.id}
                          className="dept-add-btn"
                          onClick={() => handleAddDept(d.id)}
                          disabled={deptSaving}
                        >
                          <UserPlus size={12} />
                          {d.name}
                        </button>
                      ))
                    }
                  </div>
                </div>
              )}
            </div>

            {/* Section 2.5: Holiday Calendar Management (managers/admins only) */}
            {(editForm.role === 'manager' || editForm.role === 'admin') && (
              <div className="edit-section">
                <h3 className="edit-section-title">Holiday Calendar Management</h3>
                <p className="edit-section-hint" style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-400)', marginBottom: '12px' }}>
                  Select which countries this user can manage holiday calendars for. Only applies to managers.
                </p>
                <div className="holiday-country-checkboxes" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {countries.map(c => (
                    <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: 'var(--font-size-sm)', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={holidayCountries.includes(c.id)}
                        disabled={holidayCountrySaving}
                        onChange={(e) => {
                          const newList = e.target.checked
                            ? [...holidayCountries, c.id]
                            : holidayCountries.filter(id => id !== c.id);
                          setHolidayCountries(newList);
                          setHolidayCountrySaving(true);
                          api.put(`/users/${editUser.id}/holiday-countries`, { country_ids: newList })
                            .then(() => toast.success(`Holiday management updated`))
                            .catch((err) => toast.error(err.message || 'Failed to update'))
                            .finally(() => setHolidayCountrySaving(false));
                        }}
                      />
                      <span>{c.name} ({c.code})</span>
                    </label>
                  ))}
                </div>
                {holidayCountries.length === 0 && (
                  <p className="edit-section-empty" style={{ marginTop: '8px' }}>No countries assigned. This user cannot manage holiday calendars.</p>
                )}
              </div>
            )}

            {/* Section 3: PTO */}
            <div className="edit-section">
              <h3 className="edit-section-title">PTO</h3>

              {ptoLoading ? (
                <p className="edit-section-empty">Loading PTO data...</p>
              ) : ptoBalance?.no_balance ? (
                <div className="pto-not-configured">
                  <Info size={14} />
                  <span>
                    External PTO tracking -- this user's balance is managed in another system.
                    {' '}Days entered this year: <strong>{ptoBalance.days_taken_this_year || 0}</strong>
                  </span>
                </div>
              ) : ptoBalance?.not_configured ? (
                <div className="pto-not-configured">
                  <Info size={14} />
                  <span>No PTO year has been configured yet. Set up a PTO year before managing allocations.</span>
                </div>
              ) : ptoBalance?.pto_year ? (
                <div className="pto-info-grid">
                  {/* PTO Year label */}
                  <div className="pto-year-label">
                    <Calendar size={14} />
                    <span>{ptoBalance.pto_year.label}</span>
                  </div>

                  {/* Balance summary */}
                  <div className="pto-balance-cards">
                    <div className="pto-balance-card">
                      <span className="pto-balance-value">{ptoBalance.allocation || 0}</span>
                      <span className="pto-balance-label">Allocated</span>
                    </div>
                    <div className="pto-balance-card">
                      <span className="pto-balance-value">{ptoBalance.used?.total || 0}</span>
                      <span className="pto-balance-label">Used</span>
                    </div>
                    <div className="pto-balance-card">
                      <span className="pto-balance-value">{ptoBalance.pending?.total || 0}</span>
                      <span className="pto-balance-label">Pending</span>
                    </div>
                    <div className="pto-balance-card pto-balance-card-available">
                      <span className="pto-balance-value">{ptoBalance.available?.total || 0}</span>
                      <span className="pto-balance-label">Available</span>
                    </div>
                  </div>

                  {/* Set allocation */}
                  <div className="pto-allocation-form">
                    <label className="form-label">Set Allocation (days)</label>
                    <div className="pto-allocation-input-row">
                      <input
                        type="number"
                        className="form-input"
                        value={allocationAmount}
                        onChange={(e) => setAllocationAmount(e.target.value)}
                        placeholder="e.g. 15"
                        min="0"
                        step="0.5"
                      />
                      <button
                        className="btn-primary btn-sm"
                        onClick={handleSetAllocation}
                        disabled={allocationSaving || !allocationAmount}
                      >
                        {allocationSaving ? 'Saving...' : 'Set'}
                      </button>
                    </div>
                    {prorationHint && (
                      <div className="pto-proration-hint">
                        <Info size={12} />
                        <span>
                          Mid-year hire: suggested prorated amount is <strong>{prorationHint.prorated_days}</strong> days
                          ({prorationHint.months_remaining} months remaining).
                          <button
                            className="pto-proration-accept"
                            onClick={() => setAllocationAmount(String(prorationHint.prorated_days))}
                          >
                            Use this amount
                          </button>
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Bonus section */}
                  <div className="pto-bonus-section">
                    <div className="pto-bonus-summary">
                      <Gift size={14} />
                      <span className="pto-bonus-summary-text">
                        Bonus: <strong>{ptoBalance.bonus_total || 0}</strong> awarded,{' '}
                        <strong>{ptoBalance.available?.bonus || 0}</strong> available
                      </span>
                    </div>
                    <div className="pto-bonus-form">
                      <input
                        type="number"
                        className="form-input"
                        value={bonusAmount}
                        onChange={(e) => setBonusAmount(e.target.value)}
                        placeholder="Days"
                        min="0.5"
                        step="0.5"
                        style={{ width: '80px' }}
                      />
                      <input
                        type="text"
                        className="form-input"
                        value={bonusReason}
                        onChange={(e) => setBonusReason(e.target.value)}
                        placeholder="Reason for bonus"
                        style={{ flex: 1 }}
                      />
                      <button
                        className="btn-primary btn-sm"
                        onClick={handleAwardBonus}
                        disabled={bonusSaving || !bonusAmount || !bonusReason.trim()}
                      >
                        {bonusSaving ? 'Awarding...' : 'Award'}
                      </button>
                    </div>
                  </div>

                  {/* Birthday info */}
                  {ptoBalance.birthday?.eligible && (
                    <div className="pto-birthday-info">
                      <Calendar size={12} />
                      <span>
                        Birthday day off: {ptoBalance.birthday.used ? 'Used this year' : 'Available (birth month ' + ptoBalance.birthday.birth_month + ')'}
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                <p className="edit-section-empty">Unable to load PTO data</p>
              )}
            </div>
          </>
        )}
      </Modal>

      {/* Deactivate Modal */}
      <Modal isOpen={deactivateModalOpen} onClose={() => setDeactivateModalOpen(false)} title="Deactivate User" size="small">
        {deactivateUser && (
          <>
            <p style={{ color: 'var(--gray-600)', fontSize: 'var(--font-size-sm)' }}>
              Are you sure you want to deactivate <strong>{deactivateUser.name}</strong>?
            </p>
            {loadingPreview ? (
              <p style={{ color: 'var(--gray-400)', fontSize: 'var(--font-size-xs)', marginTop: '8px' }}>Loading impact preview...</p>
            ) : deactivatePreview ? (
              <div style={{ marginTop: '8px', fontSize: 'var(--font-size-xs)', color: 'var(--gray-500)' }}>
                {deactivatePreview.pending_to_cancel.count > 0 && (
                  <p style={{ marginBottom: '4px' }}>
                    <strong>{deactivatePreview.pending_to_cancel.count}</strong> pending request{deactivatePreview.pending_to_cancel.count !== 1 ? 's' : ''} will be cancelled
                  </p>
                )}
                {deactivatePreview.future_approved_to_remove.count > 0 && (
                  <p style={{ marginBottom: '4px' }}>
                    <strong>{deactivatePreview.future_approved_to_remove.count}</strong> future approved request{deactivatePreview.future_approved_to_remove.count !== 1 ? 's' : ''} will be removed
                  </p>
                )}
                {deactivatePreview.pending_to_cancel.count === 0 && deactivatePreview.future_approved_to_remove.count === 0 && (
                  <p>No pending or future requests will be affected.</p>
                )}
              </div>
            ) : (
              <p style={{ color: 'var(--gray-500)', fontSize: 'var(--font-size-xs)', marginTop: '8px' }}>
                This will automatically cancel pending requests and remove future approved requests.
              </p>
            )}
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setDeactivateModalOpen(false)}>Cancel</button>
              <button className="btn-danger" onClick={handleDeactivateConfirm} disabled={saving}>
                {saving ? 'Deactivating...' : 'Deactivate'}
              </button>
            </div>
          </>
        )}
      </Modal>

      {/* Import from Google Workspace Modal */}
      <Modal isOpen={importModalOpen} onClose={() => setImportModalOpen(false)} title="Import from Google Workspace" size="large">
        <div className="import-modal-content">
          {/* Loading state */}
          {importLoading && (
            <div className="import-modal-loading">
              <RefreshCw size={20} className="import-spin" />
              <span>Fetching Google Workspace users...</span>
            </div>
          )}

          {/* Not configured state */}
          {!importLoading && !importConfigured && (
            <div className="import-modal-not-configured">
              <AlertTriangle size={28} strokeWidth={1.5} />
              <h3>Google Workspace Import Not Configured</h3>
              <p>{importConfigMessage || 'The Google Workspace Directory integration needs to be configured before you can import users.'}</p>
              <div className="import-modal-config-help">
                <p>Required environment variables:</p>
                <ul>
                  <li><code>GOOGLE_SERVICE_ACCOUNT_EMAIL</code></li>
                  <li><code>GOOGLE_SERVICE_ACCOUNT_KEY</code></li>
                  <li><code>GOOGLE_WORKSPACE_ADMIN_EMAIL</code></li>
                  <li><code>GOOGLE_WORKSPACE_DOMAIN</code> (defaults to frontporchsolutions.com)</li>
                </ul>
              </div>
            </div>
          )}

          {/* Error state */}
          {!importLoading && importConfigured && importError && (
            <div className="import-modal-error">
              <AlertTriangle size={18} />
              <div>
                <p>Unable to fetch Google Workspace users</p>
                <p className="import-modal-error-detail">{importError}</p>
              </div>
              <button className="btn-secondary" onClick={loadImportUsers}>
                <RefreshCw size={14} /> Retry
              </button>
            </div>
          )}

          {/* Users list */}
          {!importLoading && importConfigured && !importError && (
            <>
              <div className="import-modal-stats">
                <span className="import-modal-stat">
                  <UsersIcon size={14} />
                  {importStats.total} in workspace
                </span>
                <span className="import-modal-stat">
                  <CheckCircle size={14} />
                  {importStats.existing} already imported
                </span>
                <span className="import-modal-stat">
                  <UserPlus size={14} />
                  {importUsers.length} available
                </span>
                <button
                  className="import-modal-refresh-btn"
                  onClick={handleRefreshImport}
                  disabled={importLoading}
                  title="Refresh user list"
                >
                  <RefreshCw size={14} className={importLoading ? 'import-spin' : ''} />
                </button>
              </div>

              {importUsers.length === 0 ? (
                <div className="import-modal-empty">
                  <CheckCircle size={28} strokeWidth={1.5} />
                  <p>All Google Workspace users have already been imported.</p>
                </div>
              ) : (
                <>
                  <div className="import-modal-search-wrap">
                    <Search size={14} className="import-modal-search-icon" />
                    <input
                      type="text"
                      className="import-modal-search-input"
                      placeholder="Search by name or email..."
                      value={importSearchQuery}
                      onChange={(e) => setImportSearchQuery(e.target.value)}
                    />
                  </div>

                  <div className="import-modal-toolbar">
                    <label className="import-modal-select-all">
                      <input
                        type="checkbox"
                        checked={filteredImportUsers.length > 0 && filteredImportUsers.every(u => selectedEmails.has(u.email))}
                        onChange={toggleSelectAll}
                      />
                      Select All ({filteredImportUsers.length})
                    </label>
                    {selectedEmails.size > 0 && (
                      <button
                        className="btn-primary"
                        onClick={handleImport}
                        disabled={importing}
                      >
                        <UserPlus size={14} />
                        {importing ? 'Importing...' : `Import Selected (${selectedEmails.size})`}
                      </button>
                    )}
                  </div>

                  <div className="import-modal-user-list">
                    {filteredImportUsers.length === 0 ? (
                      <div className="import-modal-no-results">
                        No users match your search.
                      </div>
                    ) : (
                      filteredImportUsers.map(user => (
                        <label key={user.email} className="import-modal-user-item">
                          <input
                            type="checkbox"
                            checked={selectedEmails.has(user.email)}
                            onChange={() => toggleSelect(user.email)}
                          />
                          <div className="import-modal-user-avatar">
                            {user.avatar_url ? (
                              <img src={user.avatar_url} alt="" />
                            ) : (
                              <span>{(user.first_name?.[0] || user.email[0]).toUpperCase()}</span>
                            )}
                          </div>
                          <div className="import-modal-user-info">
                            <span className="import-modal-user-name">{user.name}</span>
                            <span className="import-modal-user-email">{user.email}</span>
                          </div>
                        </label>
                      ))
                    )}
                  </div>

                  <p className="import-modal-note">
                    Imported users will be in setup mode. Assign their department, country, and role, then activate them.
                  </p>
                </>
              )}

              {/* Import result */}
              {importResult && (
                <div className="import-modal-result">
                  {importResult.imported?.length > 0 && (
                    <div className="import-modal-result-success">
                      <CheckCircle size={14} />
                      {importResult.imported.length} user(s) imported
                    </div>
                  )}
                  {importResult.skipped?.length > 0 && (
                    <div className="import-modal-result-skipped">
                      <AlertTriangle size={14} />
                      {importResult.skipped.length} user(s) skipped (already exist)
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </Modal>
    </div>
  );
}

export default AdminUsers;
